import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { getFinancingRequests } from "@/services/financingRequestService";
import type { FinancingRequestDoc, FirestoreDate } from "@/types/financingRequest";
import {
  FINANCING_STATUS_LABELS,
  FINANCING_SOURCE_LABELS,
} from "@/types/financingRequest";
import { queryKeys } from "@/lib/queryKeys";
import { Search, FileText, Eye } from "lucide-react";

function formatFirestoreDate(value: FirestoreDate | null | undefined): string {
  if (value == null) return "\u2014";
  const d =
    value instanceof Date
      ? value
      : "toDate" in value && typeof (value as { toDate: () => Date }).toDate === "function"
        ? (value as { toDate: () => Date }).toDate()
        : "seconds" in value
          ? new Date((value as { seconds: number }).seconds * 1000)
          : null;
  return d
    ? d.toLocaleDateString("es-DO", {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "\u2014";
}

function getDisplayName(r: FinancingRequestDoc): string {
  const first =
    r.customerInfo?.firstName ?? r.firstName ?? "";
  const last =
    r.customerInfo?.lastName ?? r.lastName ?? "";
  const name = [first, last].filter(Boolean).join(" ").trim();
  return name || "\u2014";
}

function getDisplayPhone(r: FinancingRequestDoc): string {
  return r.customerInfo?.phone ?? r.phone ?? "\u2014";
}

function getDisplayEmail(r: FinancingRequestDoc): string {
  return r.customerInfo?.email ?? r.email ?? "";
}

function getRequestedAmount(r: FinancingRequestDoc): number {
  return r.requestedAmount ?? r.loanAmount ?? 0;
}

function getVehicleSummary(r: FinancingRequestDoc): string {
  const parts = [
    r.vehicleBrand,
    r.vehicleModel,
    r.vehicleYear ? String(r.vehicleYear) : null,
  ].filter(Boolean);
  if (parts.length) return parts.join(" ");
  return r.vehicleName ?? r.vehicleId ?? "\u2014";
}

const STATUS_VARIANT: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  pendiente: "secondary",
  en_revision: "default",
  pre_aprobado: "default",
  aprobado: "default",
  rechazado: "destructive",
  completado: "default",
  cancelado: "outline",
};

type StatusTab = "todas" | "pendiente" | "en_revision" | "aprobado" | "rechazado" | "completado";

const STATUS_TABS: { value: StatusTab; label: string }[] = [
  { value: "todas", label: "Todas" },
  { value: "pendiente", label: "Pendientes" },
  { value: "en_revision", label: "En Revisi\u00f3n" },
  { value: "aprobado", label: "Aprobadas" },
  { value: "rechazado", label: "Rechazadas" },
  { value: "completado", label: "Completadas" },
];

export function Financings() {
  const navigate = useNavigate();
  const { data: requests = [], isLoading } = useQuery({
    queryKey: queryKeys.financingRequests,
    queryFn: getFinancingRequests,
  });

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusTab>("todas");

  const filtered = requests.filter((r) => {
    // Status filter
    if (statusFilter !== "todas" && r.status !== statusFilter) return false;

    // Search filter
    const term = search.toLowerCase();
    if (!term) return true;
    const name = getDisplayName(r).toLowerCase();
    const phone = getDisplayPhone(r).toLowerCase();
    const email = getDisplayEmail(r).toLowerCase();
    const vehicle = getVehicleSummary(r).toLowerCase();
    const status = (FINANCING_STATUS_LABELS[r.status] ?? r.status).toLowerCase();
    return (
      name.includes(term) ||
      phone.includes(term) ||
      email.includes(term) ||
      vehicle.includes(term) ||
      status.includes(term)
    );
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-semibold">Solicitudes de financiamiento</h1>
        <p className="text-muted-foreground">
          Solicitudes creadas desde la app y almacenadas en Firestore
        </p>
      </div>

      {/* Status filter tabs */}
      <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusTab)}>
        <TabsList className="flex flex-wrap gap-1">
          {STATUS_TABS.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Card>
        <CardContent className="pt-6">
          <div className="mb-4 flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre, tel\u00e9fono, email, veh\u00edculo o estado..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-md"
            />
          </div>
          {isLoading ? (
            <p className="text-muted-foreground">Cargando solicitudes\u2026</p>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 py-12 text-center text-muted-foreground">
              <FileText className="h-10 w-10 opacity-50" />
              <p>
                {requests.length === 0
                  ? "No hay solicitudes de financiamiento en Firestore."
                  : "Ninguna solicitud coincide con la b\u00fasqueda."}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Contacto</TableHead>
                  <TableHead>Veh\u00edculo</TableHead>
                  <TableHead className="text-right">Solicitado</TableHead>
                  <TableHead className="text-right">Inicial</TableHead>
                  <TableHead>Plazo</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Origen</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow
                    key={r.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/financings/${r.id}`)}
                  >
                    <TableCell className="whitespace-nowrap text-muted-foreground">
                      {formatFirestoreDate(r.createdAt)}
                    </TableCell>
                    <TableCell className="font-medium">
                      {getDisplayName(r)}
                    </TableCell>
                    <TableCell>
                      <span className="block">{getDisplayPhone(r)}</span>
                      {getDisplayEmail(r) && (
                        <span className="block text-xs text-muted-foreground">
                          {getDisplayEmail(r)}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="line-clamp-2">{getVehicleSummary(r)}</span>
                      {r.vehiclePrice != null && (
                        <span className="block text-xs text-muted-foreground">
                          RD$ {r.vehiclePrice.toLocaleString("es-DO")}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      RD$ {getRequestedAmount(r).toLocaleString("es-DO")}
                    </TableCell>
                    <TableCell className="text-right">
                      RD$ {(r.downPayment ?? 0).toLocaleString("es-DO")}
                    </TableCell>
                    <TableCell>
                      {r.termMonths != null ? `${r.termMonths} meses` : "\u2014"}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={STATUS_VARIANT[r.status] ?? "outline"}
                        className="capitalize"
                      >
                        {FINANCING_STATUS_LABELS[r.status] ?? r.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {r.source
                        ? FINANCING_SOURCE_LABELS[r.source] ?? r.source
                        : "\u2014"}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" asChild onClick={(e) => e.stopPropagation()}>
                        <Link to={`/financings/${r.id}`}>
                          <Eye className="h-4 w-4" />
                        </Link>
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

    </div>
  );
}
